import { Card, Button, DatePicker, Select, Modal, Divider } from "antd";
import Table, { ColumnsType } from "antd/lib/table";
import moment from "moment-timezone";
import styles from "./index.module.less";
import { TableHelper } from "../../../util/TableHelper";
import Loading from "../../common/Loading";
import { General } from "../../../util/General";
import { useIotHubLogs } from "./index.hook";
import { IotHubLogsInterface } from "../../../shared/interface/Report/iotHubLogs.interface";
import { SyncDirectionConstants } from "../../../constants/DatabaseConstants/ParkingConstants";
import { useReactDraggable } from "../../../hooks/useReactDraggable";
import Draggable from "react-draggable";
import { CaretLeftOutlined, CaretRightOutlined } from "@ant-design/icons";

const { RangePicker } = DatePicker;

const IotHubLogs = () => {
  const config = useIotHubLogs();

  const { bounds, onStart } = useReactDraggable();

  const columns: ColumnsType<IotHubLogsInterface> = [
    {
      align: "right" as const,
      title: "Sr No.",
      dataIndex: "sr_no",
      key: "sr_no",
      render: TableHelper.renderNumberValue,
      sorter: {
        compare: TableHelper.digitSorter("sr_no")
      },
      width: "7%"
    },
    {
      align: "left" as const,
      title: "Message ID",
      dataIndex: "message_id",
      key: "message_id",
      render: (text, record) => (
        <div onClick={() => config.handleModalOpen(record)} className={`gx-link ${record.id === config.selectedRecord?.id ? styles["modal__description"] : ""}`}>
          {TableHelper.getValue(text)}
        </div>
      ),
      sorter: {
        compare: TableHelper.digitSorter("message_id")
      },
      width: "20%"
    },
    {
      align: "left" as const,
      title: "Message Payload",
      dataIndex: "message_payload",
      key: "message_payload",
      render: (text) => <div className={styles["table-message-payload"]}>{JSON.stringify(text)}</div>,
      ellipsis: true
    },
    ...(config?.selectedMode === SyncDirectionConstants.DEVICE_TO_CLOUD
      ? [
          {
            align: "right" as const,
            title: "Enqueue Time",
            dataIndex: "enqueue_time",
            key: "enqueue_time",
            render: (text: string) => (text ? moment(text).format("DD-MM-YYYY HH:mm:ss") : ""),
            sorter: {
              compare: TableHelper.dateSorter("enqueue_time")
            },
            width: "15%"
          },
          {
            align: "right" as const,
            title: "Reading Time Stamp",
            dataIndex: "reading_time_stamp",
            key: "reading_time_stamp",
            render: (text: string) => (text ? moment(text).format("DD-MM-YYYY HH:mm:ss") : ""),
            sorter: {
              compare: TableHelper.dateSorter("reading_time_stamp")
            },
            width: "15%"
          }
        ]
      : []),
    {
      align: "right" as const,
      title: "Created On",
      dataIndex: "created_on",
      key: "created_on",
      render: (text) => (text ? moment(text).format("DD-MM-YYYY HH:mm:ss") : ""),
      sorter: {
        compare: TableHelper.dateSorter("created_on")
      },
      width: "15%"
    }
  ];

  return (
    <>
      <Card className="gx-card card--height" title={null}>
        <div className={`gx-title-container`}>
          <div className={styles["title-container"]}>
            <span className="card-title-style">IotHub Logs</span>
            <div className={styles["section-1"]}>
              <Select showSearch value={config?.selectedMode} listHeight={200} onChange={(value) => config?.handleChangeStatusFilter(value)} getPopupContainer={(trigger) => trigger.parentElement} filterOption={General.searchFilterOnSelect} placeholder="Select Request Status">
                <Select.Option value={SyncDirectionConstants.CLOUD_TO_DEVICE}>Cloud To Device</Select.Option>
                <Select.Option value={SyncDirectionConstants.DEVICE_TO_CLOUD}>Device To Cloud</Select.Option>
              </Select>
              <Button onClick={config.handleRefreshList} className={`button-gradiant gx-mb-0 add-button`} loading={config.loading}>
                Refresh
              </Button>
            </div>
          </div>
          <RangePicker value={config.dateRange.fromDate && config.dateRange.toDate ? [moment(config.dateRange.fromDate, "YYYY-MM-DD HH:mm"), moment(config.dateRange.toDate, "YYYY-MM-DD HH:mm")] : null} getPopupContainer={(trigger) => trigger.parentElement || document.body} className={styles["datepicker__width"]} showTime={{ format: "HH:mm" }} format="DD-MM-YYYY HH:mm" onChange={config.handleChangeDatePicker} disabledDate={General.disabledDate} disabledTime={General.disabledRangeTime} />
        </div>

        {config.loading ? <Loading /> : <Table className="gx-table-responsive gx-mt-3" size="small" columns={columns} dataSource={config.iotHubLogList ?? []} pagination={config.pagination} bordered onChange={config.handleChangePagination} rowKey={(record) => record.sr_no} />}
      </Card>

      <Modal
        title="Message Payload"
        maskClosable={false}
        open={config.isModalVisible}
        onCancel={config.handleModalClose}
        footer={[
          <Button type="primary" key="prev" icon={<CaretLeftOutlined />} onClick={config.handlePreviousRecord} disabled={!config.selectedRecord || config.iotHubLogList.findIndex((r) => r.sr_no === config.selectedRecord?.sr_no) === 0} />,
          <Button type="primary" key="next" icon={<CaretRightOutlined />} onClick={config.handleNextRecord} disabled={!config.selectedRecord || config.iotHubLogList.findIndex((r) => r.sr_no === config.selectedRecord?.sr_no) === config.iotHubLogList.length - 1} />,
          <Button key="close" onClick={config.handleModalClose}>
            Close
          </Button>
        ]}
        width={config?.selectedMode === SyncDirectionConstants.DEVICE_TO_CLOUD ? "60%" : "40%"}
        modalRender={(modal) => (
          <Draggable bounds={bounds} handle=".ant-modal-header" onStart={(event, uiData) => onStart(event as any, uiData)}>
            {modal}
          </Draggable>
        )}
        bodyStyle={{
          height: config?.selectedMode === SyncDirectionConstants.DEVICE_TO_CLOUD ? "62vh" : "40vh",
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word"
        }}
      >
        {config.selectedRecord && (
          <div className="gx-mb-2">
            <div>
              Sr No.: <strong>{config.selectedRecord?.sr_no}</strong>
            </div>
            <div>
              Message ID: <strong>{config.selectedRecord?.message_id}</strong>
            </div>
          </div>
        )}
        <Divider />
        <pre style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(config.selectedRecord?.message_payload, null, 2)}</pre>
      </Modal>
    </>
  );
};

export default IotHubLogs;
